import * as Sentry from "@sentry/react";
import type { z } from "zod";

/**
 * 응답 검문소 — 서버 JSON 이 화면이 기대하는 뼈대인지 **받는 자리에서** 확인한다.
 *
 * 왜 있나: 화면 타입(TypeScript)은 빌드 때만 검사되고 실제 응답엔 아무 힘이 없다. 서버가
 * 목록 필드 하나를 빼거나 새 응답 종류를 늘리면 컴파일은 통과하고, 사용자 브라우저에서
 * `Cannot read properties of undefined (reading 'length')` 로 화면 한가운데가 터진다.
 * Sentry 에 같은 문구가 세 번 쌓였다(6/20 구두 관리 · 7/30 고객 가져오기 · 9/3 구간표 업로드).
 * 여기서 걸리면 빨간 오류 화면 대신 "서버 응답이 예상과 다릅니다. 새로고침해 주세요" 한 줄로 끝난다.
 *
 * 규칙(스키마를 쓸 때):
 *  - **화면이 실제로 세거나(.length) 펼치는(.map) 목록·객체만** 고정한다. 서버가 필드를 **추가**하는
 *    정상 변경에 걸리면 안 되므로 객체는 반드시 `z.looseObject`(모르는 키 통과)로 쓴다.
 *  - 값의 세부 타입(문자열 길이·enum)까지 캐지 않는다. 그건 서버 테스트 몫이다.
 *  - 실패는 **조용히 메우지 않고** 던진다. 빈 배열로 메우면 "미대응 0건" 같은 거짓 화면이 된다.
 *    대신 Sentry 에 어느 필드가 빠졌는지 남겨, 서버 쪽 계약 위반을 바로 찾게 한다.
 */
export class ContractError extends Error {
  readonly label: string;
  readonly issues: string[];
  constructor(label: string, issues: string[]) {
    super("서버 응답이 화면과 맞지 않습니다. 새 버전이 배포됐을 수 있으니 새로고침해 주세요.");
    this.name = "ContractError";
    this.label = label;
    this.issues = issues;
  }
}

/** 응답을 스키마로 확인해 그대로 돌려준다. 어긋나면 ContractError(사람이 읽을 문구)를 던진다. */
export function parseContract<T>(schema: z.ZodType<T>, data: unknown, label: string): T {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  const issues = result.error.issues.map(
    (issue) => `${issue.path.map(String).join(".") || "(root)"}: ${issue.message}`,
  );
  const error = new ContractError(label, issues);
  Sentry.captureException(error, {
    level: "warning",
    tags: { kind: "response-contract", contract: label },
    extra: { issues },
  });
  console.warn(`[contract] ${label} 응답이 계약과 다르다:`, issues);
  throw error;
}

/** fetchJson(...).then(withContract(schema, "이름")) 꼴로 붙이기 위한 커링. */
export function withContract<T>(schema: z.ZodType<T>, label: string): (data: unknown) => T {
  return (data) => parseContract(schema, data, label);
}
